import {useState} from "react";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import ImagePicker from "../general/imagePicker.view";
import LocalizationTextArea from "../../elements/localizationTextArea.element";
import LocalizationInput from "../../elements/localizationInput.element";
import {MetadataCollection} from "../../commands/metadataValue.command";
import {ImprovementManifestEntry} from "../../models/attributeManifest.model";
import {getParameterSuffix} from "../../utils/metadata.utils";
import {improvementOfReward} from "../../utils/improvements";

// The Rewards tab's City Improvements: each improvement's icon, name and description, the one
// place they are edited (the city info bar and the reward popup read them). An Improvement
// reward shows its improvement's icon unless it is given one of its own (MetadataElement).
// The improvements and their keys come from the attribute manifest.
function CityImprovementsPanel() {
    const {attributeManifest} = useApp();
    const [picking, setPicking] = useState<string | undefined>(undefined);
    const improvements = attributeManifest?.Improvements ?? [];

    return (
        <div className={'layout vertical gap box flex-grow'} style={{padding: 10, gap: 10, width: '100%', boxSizing: 'border-box'}}>
            <p className={'text header'}>City Improvements</p>
            {/* Fills the column and scrolls inside it, as the rewards palette does. */}
            {improvements.length === 0
                ? <p className={'muted-text'}>
                    No improvements in the attribute manifest — re-export it from the Unity client (dev console <code>eam</code>).
                  </p>
                : <div className={'layout vertical'} style={{gap: 10, flexGrow: 1, height: 0, overflowY: 'auto'}}>
                    {improvements.map(improvement =>
                        <ImprovementRow key={improvement.Name} improvement={improvement} onPick={setPicking}/>)}
                  </div>}
            {picking !== undefined &&
                <ImagePicker target={{kind: 'imageKey', key: picking}} currentKey={picking}
                             onClose={() => setPicking(undefined)}/>}
        </div>
    );
}

function ImprovementRow({improvement, onPick}: { improvement: ImprovementManifestEntry; onPick: (key: string) => void }) {
    const {state} = useIntegrator();
    const {getImageUrl, getLocalizationValue, getAttributeManifestEntry, attributeManifest, navigateTo} = useApp();

    const grantedBy = (state.rewards ?? []).filter(reward =>
        improvementOfReward(reward, getAttributeManifestEntry(reward.id, MetadataCollection.Rewards), attributeManifest)?.Name
            === improvement.Name);

    return (
        <div className={'layout horizontal'} style={{gap: 10, width: '100%', alignItems: 'flex-start', paddingTop: 8, borderTop: 'solid 1px var(--overlay-color)'}}>
            <img src={getImageUrl(improvement.ImageKey)} alt={improvement.Name}
                 title={`${improvement.ImageKey} — click to choose`}
                 onClick={() => onPick(improvement.ImageKey)}
                 style={{width: 48, height: 48, objectFit: 'contain', cursor: 'pointer', flexShrink: 0, backgroundColor: 'black', borderRadius: 5}}/>
            <div className={'layout vertical'} style={{flex: 1, gap: 6, minWidth: 0}}>
                <LocalizationInput id={improvement.NameKey}/>
                <LabelledText locKey={improvement.DescriptionKey} label={'Description'}/>
                <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                    Granted by{grantedBy.length === 0 ? ': no reward' : ''}
                </p>
                {grantedBy.map(reward => (
                    <p key={reward.uid} style={{fontSize: 13}}>
                        <span onClick={() => navigateTo({kind: 'metadata', uid: reward.uid})}
                              style={{cursor: 'pointer', color: 'var(--tint-color)', textDecoration: 'underline'}}>
                            {getLocalizationValue(reward.uid)}
                            {getParameterSuffix(reward, getAttributeManifestEntry(reward.id, MetadataCollection.Rewards), getLocalizationValue)}
                        </span>
                    </p>
                ))}
            </div>
        </div>
    );
}

function LabelledText({locKey, label}: { locKey: string; label: string }) {
    return (
        <div className={'layout vertical'} style={{width: '100%', gap: 2}}>
            <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>{label}</p>
            <LocalizationTextArea id={locKey}/>
        </div>
    );
}

export default CityImprovementsPanel;
